import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Plus, Trash2, Send, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

const RSVP = [
  { value: "pending", label: "Pending", color: "bg-canvas text-muted" },
  { value: "yes", label: "Attending", color: "bg-surface-2 text-[#059669]" },
  { value: "no", label: "Declined", color: "bg-[#EF4444]/10 text-[#EF4444]" },
  { value: "maybe", label: "Maybe", color: "bg-[#F59E0B]/15 text-[#F59E0B]" },
];

export default function GuestsTab({ eventId, canEdit }) {
  const [guests, setGuests] = useState([]);
  const [tables, setTables] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ name: "", email: "", party_size: 1, group_label: "", rsvp_status: "pending", meal_choice: "", table_id: "", notes: "" });

  const load = async () => {
    try {
      const [g, t] = await Promise.all([api.get(`/events/${eventId}/guests`), api.get(`/events/${eventId}/tables`)]);
      setGuests(g.data); setTables(t.data);
    } catch (e) { toast.error("Could not load guests"); }
  };
  useEffect(() => { load(); }, [eventId]);

  const add = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try {
      await api.post(`/events/${eventId}/guests`, { ...form, party_size: Number(form.party_size) || 1, table_id: form.table_id || null });
      setOpen(false);
      setForm({ name: "", email: "", party_size: 1, group_label: "", rsvp_status: "pending", meal_choice: "", table_id: "", notes: "" });
      load(); toast.success("Guest added");
    } catch (e) { toast.error("Could not add"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this guest?")) return;
    try { await api.delete(`/events/${eventId}/guests/${id}`); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  const inviteOne = async (g) => {
    if (!g.email) { toast.error("Add an email first"); return; }
    try {
      const { data } = await api.post(`/events/${eventId}/guests/${g.id}/invite`);
      toast.success(`Invite sent to ${data.to}`);
      setGuests(gs => gs.map(x => x.id === g.id ? { ...x, invited_at: new Date().toISOString() } : x));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send invite");
    }
  };

  const inviteAll = async () => {
    const targets = guests.filter(g => g.email && !g.invited_at);
    if (targets.length === 0) { toast.error("No guests with an email left to invite"); return; }
    if (!confirm(`Send RSVP invites to ${targets.length} guest${targets.length === 1 ? "" : "s"}?`)) return;
    try {
      const { data } = await api.post(`/events/${eventId}/guests/invite-all`);
      if (data.sent > 0) toast.success(`Sent ${data.sent} invite${data.sent === 1 ? "" : "s"}`);
      if (data.failed?.length) toast.error(`${data.failed.length} failed to send`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send invites");
    }
  };

  const copyInviteLink = async (g) => {
    if (!g.rsvp_token) { toast.error("This guest has no invite token yet — send an invite to generate one."); return; }
    const url = `${window.location.origin}/rsvp/${eventId}?token=${encodeURIComponent(g.rsvp_token)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`RSVP link copied — ${g.name}`);
    } catch {
      toast.error("Clipboard blocked — link: " + url);
    }
  };

  const update = async (id, field, value) => {
    try {
      await api.patch(`/events/${eventId}/guests/${id}`, { [field]: field === "party_size" ? Number(value) : (value || null) });
      setGuests(guests.map(g => g.id === id ? { ...g, [field]: field === "party_size" ? Number(value) : value } : g));
    } catch (e) { toast.error("Could not update"); }
  };

  const bouncedCount = guests.filter(g => g.email_status === "bounced").length;
  const visible = guests.filter(g => {
    if (filter === "all") return true;
    if (filter === "bounced") return g.email_status === "bounced";
    return g.rsvp_status === filter;
  });
  const totals = {
    invited: guests.reduce((s, g) => s + (g.party_size || 1), 0),
    yes: guests.filter(g => g.rsvp_status === "yes").reduce((s, g) => s + (g.party_size || 1), 0),
    no: guests.filter(g => g.rsvp_status === "no").reduce((s, g) => s + (g.party_size || 1), 0),
    pending: guests.filter(g => g.rsvp_status === "pending").reduce((s, g) => s + (g.party_size || 1), 0),
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2">MOD.GUESTS / RSVP</div>
        <h2 className="font-display text-xl md:text-2xl font-bold text-app tracking-tight leading-none mb-4">
          Guest list · <span className="font-mono tabular-nums">{totals.invited.toString().padStart(2, "0")}</span> invited
        </h2>
      </div>
      <div className="grid grid-cols-4 gap-4">
        <Stat label="Invited" v={totals.invited} />
        <Stat label="Attending" v={totals.yes} accent="text-[#059669]" />
        <Stat label="Declined" v={totals.no} accent="text-[#EF4444]" />
        <Stat label="Pending" v={totals.pending} />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {[["all","All"], ...RSVP.map(r => [r.value, r.label])].map(([v,l]) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              data-testid={`guest-filter-${v}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm ${filter === v ? "bevel-btn-primary text-white" : "text-muted hover:bg-surface-2"}`}
            >{l}</button>
          ))}
          {bouncedCount > 0 && (
            <button
              key="bounced"
              onClick={() => setFilter("bounced")}
              data-testid="guest-filter-bounced"
              title="Guests whose invite email bounced"
              className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm inline-flex items-center gap-1.5 border ${filter === "bounced" ? "bg-[#EF4444] text-white border-[#EF4444]" : "border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444]/10"}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] live-pulse" aria-hidden />
              Bounced [{bouncedCount}]
            </button>
          )}
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button
              data-testid="invite-all-guests-btn"
              variant="outline"
              onClick={inviteAll}
              className="rounded-sm border-[#0A0A0A]/30 text-app hover:bg-surface-2"
              title="Send RSVP invites to guests with an email who haven't been invited yet"
            >
              <Send size={14} strokeWidth={1.5} className="mr-2" /> Send invites
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-guest-btn" className="rounded-sm bg-app hover:bg-[#000000]">
                <Plus size={16} strokeWidth={1.5} className="mr-2" /> Add guest
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-surface rounded-sm max-w-lg">
              <DialogHeader><DialogTitle className="font-serif text-2xl text-app">Add guest</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <F label="Name"><Input data-testid="guest-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
                  <F label="Party size"><Input type="number" min={1} value={form.party_size} onChange={(e) => setForm({ ...form, party_size: e.target.value })} /></F>
                </div>
                <F label="Group (e.g. Bride's family)"><Input value={form.group_label} onChange={(e) => setForm({ ...form, group_label: e.target.value })} /></F>
                <div className="grid grid-cols-2 gap-3">
                  <F label="RSVP">
                    <Select value={form.rsvp_status} onValueChange={(v) => setForm({ ...form, rsvp_status: v })}>
                      <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{RSVP.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Meal"><Input value={form.meal_choice} onChange={(e) => setForm({ ...form, meal_choice: e.target.value })} placeholder="e.g. Vegetarian" /></F>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="guest-submit" onClick={add} className="rounded-sm bg-app hover:bg-[#000000]">Add guest</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <div className="bevel border-hair rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-hair">
                {["Name", "Group", "Party", "RSVP", "Meal", "Table", ""].map(h => (
                  <th key={h} className="text-left eyebrow text-muted px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-muted font-light">No guests here.</td></tr>}
              {visible.map(g => {
                const rsvp = RSVP.find(r => r.value === g.rsvp_status);
                return (
                  <tr key={g.id} data-testid={`guest-row-${g.id}`} className="border-b border-hair/60 hover:bg-canvas/40">
                    <td className="px-4 py-3">
                      <div className="text-app flex items-center gap-2 flex-wrap">
                        <span>{g.name}</span>
                        {g.invited_at && <span className="eyebrow text-[10px] px-1.5 py-0.5 rounded-sm bg-surface-2 text-[#059669]" title={`Invited ${new Date(g.invited_at).toLocaleString()}`}>Invited</span>}
                        {g.email_status && <EmailStatusChip status={g.email_status} at={g.email_status_at} />}
                      </div>
                      {g.email && <div className="text-xs text-muted font-light">{g.email}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted">{g.group_label || "—"}</td>
                    <td className="px-4 py-3 text-sm text-app">{g.party_size}</td>
                    <td className="px-4 py-3">
                      {canEdit ? (
                        <Select value={g.rsvp_status} onValueChange={(v) => update(g.id, "rsvp_status", v)}>
                          <SelectTrigger className={`w-32 h-8 rounded-sm text-xs ${rsvp?.color}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{RSVP.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : <span className={`px-2 py-0.5 eyebrow rounded-sm ${rsvp?.color}`}>{rsvp?.label}</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted">{g.meal_choice || "—"}</td>
                    <td className="px-4 py-3">
                      {canEdit ? (
                        <Select value={g.table_id || "none"} onValueChange={(v) => update(g.id, "table_id", v === "none" ? "" : v)}>
                          <SelectTrigger className="w-32 h-8 rounded-sm text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">—</SelectItem>
                            {tables.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (tables.find(t => t.id === g.table_id)?.name || "—")}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1 justify-end">
                        {g.rsvp_token && (
                          <button
                            onClick={() => copyInviteLink(g)}
                            data-testid={`copy-invite-link-${g.id}`}
                            title="Copy RSVP link (paste into WhatsApp, DM, text)"
                            className="p-1 text-muted hover:text-app"
                          >
                            <LinkIcon size={14} strokeWidth={1.5} />
                          </button>
                        )}
                        {canEdit && g.email && (
                          <button
                            onClick={() => inviteOne(g)}
                            data-testid={`invite-guest-${g.id}`}
                            title={g.invited_at ? "Resend RSVP invite" : "Send RSVP invite"}
                            className={`p-1 ${g.invited_at ? "text-[#059669]" : "text-muted"} hover:text-app`}
                          >
                            <Send size={14} strokeWidth={1.5} />
                          </button>
                        )}
                        {canEdit && <button onClick={() => del(g.id)} data-testid={`delete-guest-${g.id}`} className="text-muted hover:text-[#EF4444] p-1"><Trash2 size={14} strokeWidth={1.5} /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, v, accent }) {
  return (
    <div className="bevel border-hair rounded-sm p-5">
      <div className="eyebrow mb-2">{label}</div>
      <div className={`font-serif text-3xl ${accent || "text-app"}`}>{v}</div>
    </div>
  );
}

function F({ label, children }) {
  return (<div><Label className="eyebrow block mb-2">{label}</Label>{children}</div>);
}

const STATUS_LABEL = {
  sent: { label: "Sent", cls: "bg-surface-2 text-muted" },
  delivered: { label: "Delivered", cls: "bg-surface-2 text-[#059669]" },
  opened: { label: "Opened", cls: "bg-surface-2 text-app" },
  clicked: { label: "Clicked", cls: "bevel-btn-primary text-white" },
  bounced: { label: "Bounced", cls: "bg-[#FEE2E2] text-[#EF4444]" },
  complained: { label: "Complained", cls: "bg-[#FEE2E2] text-[#EF4444]" },
  delayed: { label: "Delayed", cls: "bg-[#FEF3C7] text-[#F59E0B]" },
};

function EmailStatusChip({ status, at }) {
  const s = STATUS_LABEL[status];
  if (!s) return null;
  return (
    <span
      className={`eyebrow text-[10px] px-1.5 py-0.5 rounded-sm ${s.cls}`}
      title={at ? `${s.label} · ${new Date(at).toLocaleString()}` : s.label}
    >
      {s.label}
    </span>
  );
}

